import { Avatar } from "../components/Avatar";
import { useState, type ReactNode } from "react";
import { supabase } from "../lib/supabase";
import { href } from "../lib/route";
import { AutoApplySwitch } from "../components/AutoApplySwitch";
import { VisibleRangeControl } from "../components/VisibleRangeControl";
import { LocalClockLine } from "../components/LocalClockLine";
import { endCoaching, endCoachingWarning, type UpdatableClient } from "../lib/endCoaching";
import type { VisibleRange } from "../lib/visibleRange";
import type { InjuryLine } from "../lib/injuries";
import {
  clientCardLines, equipmentLine, lastTrained, rankedMains, specificGoals, trainingRows, type Row,
} from "../lib/profileTab";

/* 🔴 W72 STEP 4 — PROFILE, "WHO IS THIS" (Proposal 4, his yes). Rows on the ground under small-caps headings, the
 * engine's inputs in the engine's order: TRAINING · GOALS · INJURIES AND LIMITS (read-only — the engine reads it) ·
 * EQUIPMENT · NEXT WEEK (the auto-apply switch step 2 moved here, restyled, still its ONE mount) — with the client card
 * on the right and `End coaching…` as the one destructive TEXT link at the foot. Every sentence is `lib/profileTab.ts`'s.
 * ⚠️ YOUR NOTE (the coach's own line on the client, `coach_clients.coach_note`) IS NOT BUILT: it is `LAUNCH.md` §6,
 * row 2026-09-25 ~00:45, item (4) — his word on the feature. The existing private notes card (`coach_client_notes`,
 * `COACH_NOTES_ENABLED`) keeps its place in the side column meanwhile; the page does not grow a second notes surface.
 * ⚠️ REDESIGNED AWAY: `Not shown here` — its "Body metrics: not readable by a coach" has been FALSE since
 * `body_measurements_coach_read` (2026-09-14); the Progress tab reads the weigh-ins. A stale sentence is not kept. */

type Profile = {
  goals: string[]; mainGoals: string[]; trainingDaysPerWeek: number | null; sessionLengthMinutes: number | null;
  unitSystem: string | null; injuries: InjuryLine[]; timezone: string | null; selectedEquipment: string[];
  equipmentAssumed: boolean; trainingStartDate: string | null; neverTrained: boolean | null; trainingBreakMonths: number | null;
};

export function ClientProfile({ clientId, name, photoUrl = null, profile, linkId, linkStatus, autoApplyNextWeek, visibleRange, invitedEmail,
  acceptedAt, days, notes, onChanged }: {
  clientId: string; name: string; photoUrl?: string | null; profile: Profile | null; linkId: string; linkStatus: string; autoApplyNextWeek: boolean;
  visibleRange: VisibleRange | undefined; invitedEmail: string | null; acceptedAt: string | null; days: ReadonlySet<string>;
  /** The existing private-notes card, composed by the page (its save lives there). */
  notes: ReactNode; onChanged: () => void;
}) {
  const [ending, setEnding] = useState(false);
  const [endError, setEndError] = useState<string | null>(null);
  const active = linkStatus === "active";
  const eq = equipmentLine(profile?.selectedEquipment ?? [], profile?.equipmentAssumed ?? false);
  const mains = rankedMains(profile?.mainGoals ?? []);
  const specifics = specificGoals(profile?.goals ?? []);

  return (
    <div className="profile-tab">
      <div className="profile-main">
        {profile === null ? (
          <p className="pempty">They have not finished their setup yet, so there is nothing of theirs to read.</p>
        ) : (
          <>
            <Section id="ptraining" title="Training"><Rows rows={trainingRows(profile)} /></Section>

            <Section id="pgoals2" title="Goals">
              <dl className="prow-list">
                <div className="prow">
                  <dt>Ranked mains</dt>
                  <dd>{mains.length === 0 ? <Absent /> : (
                    <ol className="prow-ranked">{mains.map((m, i) => <li key={m}><span>{i + 1}</span>{m}</li>)}</ol>
                  )}</dd>
                </div>
                <div className="prow">
                  <dt>Their goals</dt>
                  <dd>{specifics.length === 0 ? <Absent /> : specifics.join(" · ")}</dd>
                </div>
              </dl>
            </Section>

            <Section id="plimits" title="Injuries and limits">
              {profile.injuries.length === 0 ? <p className="prow-empty">None recorded.</p> : (
                <ul className="prow-injuries">
                  {profile.injuries.map((i) => (
                    <li key={i.key} className="prow">
                      <span className="prow-injury">{i.label}<span className={`prow-severity${i.severity === "Severe" ? " is-severe" : ""}`}> · {i.severity}</span></span>
                      {i.details.length > 0 && <span className="prow-detail">{i.details.join(" · ")}</span>}
                    </li>
                  ))}
                </ul>
              )}
              <p className="prow-foot">What they recorded, read-only. The engine reads this and filters their exercises.</p>
            </Section>

            <Section id="pequip" title="Equipment">
              <p className="prow-equipment">
                {eq.shown.length === 0 ? <Absent /> : <>{eq.shown.join(" · ")}{eq.more > 0 && <span className="prow-more"> · + {eq.more} more</span>}</>}
              </p>
              {eq.note !== null && eq.shown.length > 0 && <p className="prow-foot">{eq.note}</p>}
            </Section>
          </>
        )}

        <Section id="pnext" title="Next week">
          <div className="prow prow-switch">
            {/* ⚠️ THE ONE MOUNT of the auto-apply switch (step 2 moved it here; `tab` holds one value, so it is never two
                local copies of one database fact). */}
            <AutoApplySwitch linkId={linkId} initial={autoApplyNextWeek} canEnable={active} />
          </div>
          <div className="prow prow-range">
            <span className="prow-label">What they can see</span>
            <VisibleRangeControl linkId={linkId} initial={visibleRange} disabled={!active} />
          </div>
        </Section>

        <footer className="profile-foot">
          {active ? (
            <>
              {/* THE COST, IN THE SENTENCE — D-V8.2. Nothing is deleted; what STOPS is sending. */}
              <button type="button" className="profile-end" disabled={ending}
                      onClick={() => {
                        if (!window.confirm(endCoachingWarning(1))) return;
                        setEnding(true); setEndError(null);
                        void endCoaching(supabase as unknown as UpdatableClient, [linkId]).then((r) => {
                          setEnding(false);
                          if (r.error !== null) { setEndError(r.error); return; }
                          onChanged();
                        });
                      }}>
                {ending ? "Ending…" : "End coaching…"}
              </button>
              <span className="prow-foot">They keep their history and you both keep reading the thread; nobody can send.</span>
              {endError !== null && <p className="signin-form-error" role="alert">{endError}</p>}
            </>
          ) : (
            <span className="prow-foot">This relationship is <strong>{linkStatus}</strong>, so there is nothing to end.</span>
          )}
        </footer>
      </div>

      <aside className="profile-side">
        <section className="pcard" aria-labelledby="pcard-h">
          <Avatar name={name} photoUrl={photoUrl} className="pcard-avatar" />
          <h2 id="pcard-h" className="pcard-name">{name}</h2>
          {profile !== null && <LocalClockLine zone={profile.timezone} className="prow-foot" />}
          <Rows rows={clientCardLines({ email: invitedEmail, acceptedAt, lastTrainedDayKey: lastTrained(days) })} />
          <a className="pcard-message" href={href({ name: "thread", clientId })}>Message</a>
        </section>
        {notes}
      </aside>
    </div>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section className="psection" aria-labelledby={id}>
      <h2 id={id} className="pheading">{title}</h2>
      {children}
    </section>
  );
}

function Rows({ rows }: { rows: readonly Row[] }) {
  return (
    <dl className="prow-list">
      {rows.map((r) => (
        <div key={r.label} className="prow">
          <dt>{r.label}</dt>
          <dd>{r.value ?? <Absent />}</dd>
        </div>
      ))}
    </dl>
  );
}

function Absent() {
  return <span className="prow-absent">Not set</span>;
}
